import type { RestConnectorConfig } from "oi-ecp-data-connector";
import type { APIResponse, RestErrorObject } from "oi-ecp-data-connector";
import { RestDataConnector } from "oi-ecp-data-connector";
import { getToken } from "../config/getToken";
import { withRequestDeadline } from "../utils/requestDeadline";

type ConnectorResult = APIResponse<unknown> | RestErrorObject;

const isRecord = (value: unknown): value is Record<string, unknown> =>
  typeof value === "object" && value !== null;

const getStringProperty = (
  value: unknown,
  property: string,
): string | undefined => {
  if (!isRecord(value)) {
    return undefined;
  }
  return typeof value[property] === "string" ? value[property] : undefined;
};

/**
 * Shared base connector functionality:
 * - enforces consistent error handling (throw on non-2xx)
 * - provides a helper for Authorization header injection
 *
 * Domain connectors (clients/templates) should extend this.
 */
export class DataConnectorBase extends RestDataConnector {
  constructor(cfg: RestConnectorConfig) {
    super(cfg);
  }

  protected async setEndpointAuthHeader(endpoint: string): Promise<void> {
    const endpointConfig = this.getConfig().restApiConfig?.[endpoint];
    if (!endpointConfig) return;

    const token = await getToken();
    const current = (endpointConfig.headers ?? {}) as Record<string, string>;
    if (token) {
      endpointConfig.headers = {
        ...current,
        Authorization: `Bearer ${token}`,
      };
      return;
    }

    const rest = { ...current };
    delete rest.Authorization;
    delete rest.authorization;
    endpointConfig.headers = rest;
  }

  protected async restCallStrict<T>(
    endpoint: string,
    body: unknown,
    params?: Record<string, unknown>,
  ): Promise<T> {
    const result: ConnectorResult = await withRequestDeadline(() =>
      this.restCall(endpoint, body as Record<string, unknown> | null, params),
    );

    // Network failures (e.g. offline) often come back as an object without statusCode.
    // Ensure those are treated as failures instead of being stored as success data.
    // Example: { error: {}, additionalContext: { error: {} }, where: ... }
    if ("error" in result) {
      throw new Error(
        result.error instanceof Error ? result.error.message : "Request failed",
      );
    }

    // Some connector implementations resolve (instead of throw) on 4xx/5xx and include
    // statusCode/url in the payload.
    if (result.statusCode < 200 || result.statusCode >= 300) {
      const message =
        getStringProperty(result.data, "error") ??
        `Request failed with status ${result.statusCode}`;
      throw new Error(message);
    }

    // Extra safety if API returns 200 with an embedded error.
    const embeddedError = isRecord(result.data) ? result.data.error : undefined;
    if (typeof embeddedError === "string") {
      throw new Error(embeddedError);
    }
    if (isRecord(embeddedError)) {
      throw new Error(
        getStringProperty(embeddedError, "message") ?? "Request failed",
      );
    }

    // Many endpoints return a standard envelope: { data: <payload>, statusCode, url }
    // For app code we want the payload, not the envelope.
    if ("data" in result) {
      return result.data as T;
    }

    return result as T;
  }
}
